import {Alert, HPButton} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {dataTestId} from '@hproduce/suite-core'
import {Check, Close, Replay} from '@mui/icons-material'
import {Stack} from '@mui/material'
import {useCallback, useMemo} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useEditOptimizerSettings, useOptimizerSettings} from '../../hooks'
import {useDialog} from '../../providers'
import {requiredValidator} from '../../utils'

import {validateOptimizeFromTime} from './optimizerPriorities.helpers'
import {OptimizerPrioritiesLegend} from './OptimizerPrioritiesLegend'
import {OptimizerPrioritiesSlidersInput} from './OptimizerPrioritiesSlidersInput'
import {OptimizerPrioritiesTriangleInput} from './OptimizerPrioritiesTriangleInput'
import {OptimizerSettingsHorizonSlider} from './OptimizerSettingsHorizonSlider'
import {OptimizerSettingsTimeoutInput} from './OptimizerSettingsTimeoutInput'
import {OptimizerSettingsTimePicker} from './OptimizerSettingsTimePicker'

export const OptimizerSettingsDialogContent = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantId} = useUpmPlantContext()
  const shouldShowTimeframeSettings = useCommonFeatureFlag(FeatureFlagKey.OptimizerStatusDisplay, {
    plantId
  })

  const {data: optimizerSettings} = useOptimizerSettings()
  if (!optimizerSettings) {
    throw new Error('BUG: Optimizer settings should be loaded before opening the dialog')
  }

  const {editOptimizerSettings, isLoading} = useEditOptimizerSettings()

  const {
    control,
    formState: {isDirty, isValid},
    reset,
    handleSubmit,
    watch
  } = useForm({
    defaultValues: {...optimizerSettings},
    mode: 'onChange'
  })

  const currentHorizonDays = watch('horizonDays')
  const currentExpectedDuration = optimizerSettings.expectedDurations[currentHorizonDays.toString()]

  const {closeDialog} = useDialog()

  const submit = useMemo(
    () => handleSubmit((optimizerSettings) => editOptimizerSettings(optimizerSettings)),
    [handleSubmit, editOptimizerSettings]
  )

  const handleSaveSettings = useCallback(() => {
    void submit()
    closeDialog()
  }, [closeDialog, submit])

  const handleResetSettings = useCallback(() => reset(), [reset])

  const shouldDisableSaveButton = !isDirty || !isValid || isLoading

  return (
    <Stack spacing={2.75} sx={{maxWidth: ({spacing}) => spacing(90)}}>
      <Alert
        accentColor="info"
        content={t('optimizer.optimizerSettingsInfo')}
        {...dataTestId('optimizer_settings_info')}
      />

      {/* Triangle priorities */}
      <Stack direction="row" spacing={2} sx={{width: 1}}>
        <Controller
          name="priorities"
          control={control}
          render={({field: {value, onChange}}) => (
            <OptimizerPrioritiesTriangleInput
              initialValue={optimizerSettings.priorities}
              value={value}
              setValue={onChange}
            />
          )}
        />
        <Stack spacing={2} sx={{alignSelf: 'center', flex: 1}}>
          <OptimizerPrioritiesLegend />
          <Controller
            name="priorities"
            control={control}
            render={({field: {value, onChange}}) => {
              return <OptimizerPrioritiesSlidersInput value={value} setValue={onChange} />
            }}
          />
        </Stack>
      </Stack>

      {/* Timeframe settings */}
      {shouldShowTimeframeSettings && (
        <Stack spacing={1.5} {...dataTestId('optimizer_timeframe_settings')}>
          <Controller
            name="horizonDays"
            control={control}
            rules={{...requiredValidator(t)}}
            render={({field: {value, onChange}}) => (
              <OptimizerSettingsHorizonSlider
                optimizerSettings={optimizerSettings}
                value={value}
                setValue={onChange}
              />
            )}
          />
          <Stack direction="row" spacing={1.5}>
            <Controller
              name="timeLimit"
              control={control}
              rules={{...requiredValidator(t)}}
              render={({field: {value, onChange}}) => (
                <OptimizerSettingsTimeoutInput
                  errorMessage={
                    value < currentExpectedDuration.max ? t('optimizer.timeoutWarning') : undefined
                  }
                  value={value}
                  setValue={onChange}
                />
              )}
            />
            <Controller
              name="optimizeFrom"
              control={control}
              rules={{
                ...requiredValidator(t),
                validate: validateOptimizeFromTime(t)
              }}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <OptimizerSettingsTimePicker
                  label={t('optimizer.optimizeFrom')}
                  errorMessage={error && error.message}
                  value={value}
                  setValue={onChange}
                  data-test-id="optimize-from-time-picker"
                />
              )}
            />
          </Stack>
        </Stack>
      )}

      {/* Action buttons */}
      <Stack spacing={1.5} direction="row" justifyContent="space-between" sx={{width: 1}}>
        <HPButton
          {...dataTestId('reset_button')}
          backgroundMode="light"
          variant="text"
          startIcon={<Replay />}
          onClick={handleResetSettings}
          sx={{alignSelf: 'flex-start'}}
        >
          {t('optimizer.resetToDefault')}
        </HPButton>
        <Stack direction="row" spacing={1.5}>
          <HPButton
            {...dataTestId('cancel_button')}
            backgroundMode="light"
            variant="text"
            startIcon={<Close />}
            onClick={closeDialog}
          >
            {t('common.cancel')}
          </HPButton>
          <HPButton
            {...dataTestId('save_button')}
            variant="contained"
            backgroundMode="light"
            startIcon={<Check />}
            onClick={handleSaveSettings}
            disabled={shouldDisableSaveButton}
          >
            {t('common.save')}
          </HPButton>
        </Stack>
      </Stack>
    </Stack>
  )
}
